import { 
  signInWithPopup, 
  signOut as firebaseSignOut,
  onAuthStateChanged,
  getIdToken
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import { base_url } from '../utils/network';

class AuthService {
  constructor() {
    this.user = null;
    this.customToken = null;
    this.apiBaseUrl = base_url;
    this.listeners = [];
    this.isInitialized = false;
    this.authCache = null;
    this.cacheExpiry = null;
  }

  // Load auth cache from localStorage
  loadAuthCache() {
    try {
      const cached = localStorage.getItem('firebase_auth_cache');
      if (cached) {
        const parsed = JSON.parse(cached);
        // Check if cache is still valid (not expired)
        if (parsed.expiry && Date.now() < parsed.expiry) {
          this.authCache = parsed;
          this.cacheExpiry = parsed.expiry;
          console.log('AuthService: Loaded auth from localStorage cache');
          return true;
        } else {
          console.log('AuthService: Cached auth expired, clearing cache');
          localStorage.removeItem('firebase_auth_cache');
        }
      }
    } catch (error) {
      console.log('AuthService: Error loading auth cache:', error);
      localStorage.removeItem('firebase_auth_cache');
    }
    return false;
  }

  // Save auth cache to localStorage
  saveAuthCache() {
    try {
      const cacheData = {
        user: this.user,
        customToken: this.customToken,
        expiry: Date.now() + (45 * 60 * 1000) // 45 minutes
      };
      localStorage.setItem('firebase_auth_cache', JSON.stringify(cacheData));
      this.cacheExpiry = cacheData.expiry;
    } catch (error) {
      console.log('AuthService: Error saving auth cache:', error);
    }
  }

  // Clear auth cache
  clearAuthCache() {
    this.authCache = null;
    this.cacheExpiry = null;
    localStorage.removeItem('firebase_auth_cache');
  }

  // Initialize Firebase Auth
  async initialize() {
    if (this.isInitialized) return;

    try {
      // Try to load from cache first
      const hasValidCache = this.loadAuthCache();
      
      if (hasValidCache && this.authCache) {
        console.log('AuthService: Using cached auth, skipping Firebase Auth check');
        this.user = this.authCache.user;
        this.customToken = this.authCache.customToken;
        this.notifyListeners();
        this.isInitialized = true;
        return Promise.resolve();
      }

      // Listen for auth state changes
      onAuthStateChanged(auth, (firebaseUser) => {
        console.log('Firebase Auth state changed:', firebaseUser ? 'User signed in' : 'User signed out');
        if (firebaseUser) {
          // User is signed in - always handle auth state change to ensure backend sync
          console.log('Firebase user detected, handling auth state change');
          this.handleAuthStateChange(firebaseUser);
        } else {
          // User is signed out
          console.log('Firebase user signed out, handling sign out');
          this.handleSignOut();
        }
      });

      this.isInitialized = true;
      
      // Wait for Firebase Auth to initialize and check current user
      return new Promise((resolve) => {
        const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
          unsubscribe(); // Unsubscribe after first check
          resolve();
        });
        
        // Fallback timeout in case Firebase Auth doesn't respond
        setTimeout(() => {
          resolve();
        }, 1000);
      });
    } catch (error) {
      console.error('Failed to initialize Firebase Auth:', error);
      throw error;
    }
  }

  // Handle Firebase auth state change
  handleAuthStateChange = async (firebaseUser) => {
    try {
      // Check if we already have a valid user session
      const existingUser = this.getCurrentUser();
      const existingToken = this.getCurrentToken();
      
      if (existingUser && existingToken) {
        console.log('User already authenticated, skipping backend call');
        // Still notify listeners to update UI
        this.notifyListeners();
        return { success: true, user: this.user };
      }
      
      // Get ID token from Firebase
      const idToken = await getIdToken(firebaseUser);
      console.log('Got ID token from Firebase:', idToken.substring(0, 50) + '...');
      
      const apiUrl = `${this.apiBaseUrl}auth/google`;
      console.log('Calling backend API:', apiUrl);
      
      // Send ID token to backend for verification
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          idToken: idToken
        })
      });

      console.log('Backend response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Backend response data:', data);
      
      // Check if response has the expected structure
      if (data.success && data.status === 200 && data.data) {
        this.customToken = data.data.customToken;
        this.user = data.data.user;
        
        // Store in localStorage for persistence
        localStorage.setItem('customToken', data.data.customToken);
        localStorage.setItem('user', JSON.stringify(data.data.user));
        
        // Save to auth cache to avoid Firebase API calls on reload
        this.saveAuthCache();
        
        // Notify listeners
        this.notifyListeners();
        
        return { success: true, user: this.user };
      } else if (data.success && data.data) {
        // Fallback for different response structure
        console.log('Using fallback response structure');
        this.customToken = data.data.customToken || data.data.token;
        this.user = data.data.user || data.data;
        
        // Store in localStorage for persistence
        localStorage.setItem('customToken', this.customToken);
        localStorage.setItem('user', JSON.stringify(this.user));
        
        // Notify listeners
        this.notifyListeners();
        
        return { success: true, user: this.user };
      } else {
        console.error('Unexpected response structure:', data);
        throw new Error(data.message || 'Backend authentication failed - unexpected response structure');
      }
    } catch (error) {
      console.error('Auth state change error:', error);
      throw error;
    }
  };

  // Sign in with Google using Firebase
  async signIn() {
    try {
      await this.initialize();
      
      const result = await signInWithPopup(auth, googleProvider);
      console.log('Firebase sign-in successful:', result.user);
      
      // Explicitly call backend API with the ID token
      await this.handleAuthStateChange(result.user);
      
      return result.user;
    } catch (error) {
      console.error('Firebase sign-in error:', error);
      throw error;
    }
  }

  // Sign in with backend (called by auth state change)
  signInWithBackend = async (idToken) => {
    try {
      const response = await fetch(`${this.apiBaseUrl}auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          idToken: idToken
        })
      });

      const data = await response.json();
      
      if (data.success && data.status === 200) {
        this.customToken = data.data.customToken;
        this.user = data.data.user;
        
        // Store in localStorage for persistence
        localStorage.setItem('customToken', data.data.customToken);
        localStorage.setItem('user', JSON.stringify(data.data.user));
        
        // Notify listeners
        this.notifyListeners();
        
        return { success: true, user: this.user };
      } else {
        throw new Error(data.message || 'Sign-in failed');
      }
    } catch (error) {
      console.error('Backend sign-in error:', error);
      throw error;
    }
  };

  // Handle sign out
  handleSignOut() {
    this.user = null;
    this.customToken = null;
    localStorage.removeItem('customToken');
    localStorage.removeItem('user');
    this.clearAuthCache();
    this.notifyListeners();
  }

  // Sign out
  async signOut() {
    try {
      await firebaseSignOut(auth);
      this.handleSignOut();
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  }

  // Get current user
  getCurrentUser() {
    if (!this.user) {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        this.user = JSON.parse(storedUser);
      }
    }
    return this.user;
  }

  // Get current token
  getCurrentToken() {
    if (!this.customToken) {
      this.customToken = localStorage.getItem('customToken');
    }
    return this.customToken;
  }

  // Check if user is authenticated
  isAuthenticated() {
    return this.getCurrentUser() !== null && this.getCurrentToken() !== null;
  }

  // Make authenticated API calls
  makeAuthenticatedRequest = async (url, options = {}) => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('Not authenticated');
    }

    // Get fresh ID token for each request
    const idToken = await getIdToken(currentUser);

    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.status === 401) {
      this.signOut();
      throw new Error('Session expired. Please sign in again.');
    }

    return response;
  };

  // Restore session from localStorage
  restoreSession() {
    const token = localStorage.getItem('customToken');
    const user = localStorage.getItem('user');
    
    if (token && user) {
      this.customToken = token;
      this.user = JSON.parse(user);
      return true;
    }
    return false;
  }

  // Add authentication state listener
  addAuthStateListener(callback) {
    this.listeners.push(callback);
  }

  // Remove authentication state listener
  removeAuthStateListener(callback) {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  // Notify all listeners of auth state change
  notifyListeners() {
    this.listeners.forEach(listener => {
      try {
        listener(this.user);
      } catch (error) {
        console.error('Error in auth state listener:', error);
      }
    });
  }
}

// Create and export a singleton instance
const authService = new AuthService();
export default authService;
